<template>
  <v-card class="rounded-2">
    <div>
      <v-card-title class="pt-4">
        <div class="d-flex align-center flex-wrap ga-3">
          <span
            class="text-primary text-h6 font-weight-bold d-flex align-center"
          >
            Sent Messages
            <v-chip size="small" color="primary" variant="tonal" class="ml-2">
              {{ messagesList.length }}
            </v-chip>
          </span>
          <v-spacer />
          <v-text-field
            v-model="searchTerm"
            append-icon="mdi-magnify"
            label="Search Messages"
            single-line
            clearable
            hide-details
            density="compact"
            variant="outlined"
            class="search-field"
          ></v-text-field>
        </div>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="messagesList"
        :items-per-page="10"
        :search="searchTerm"
        :header-props="headerProps"
        :mobile-breakpoint="0"
        class="elevation-1 table-scroll"
        no-data-text="No messages found"
      >
        <!--Headers slot-->
        <template
          v-slot:headers="{ columns, isSorted, getSortIcon, toggleSort }"
        >
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :class="column.class"
              @click="() => toggleSort(column)"
            >
              {{ column.title }}
              <template v-if="isSorted(column)">
                <v-icon :icon="getSortIcon(column)" size="sm" />
              </template>
            </th>
          </tr>
        </template>
        <!--Items slot-->
        <template v-slot:item="{ item }">
          <tr :class="{ 'bg-blue-lighten-5': isRowBeingEdited(item) }">
            <!--Type slot-->
            <td>
              <v-tooltip location="top">
                <template v-slot:activator="{ props: activatorProps }">
                  <v-chip
                    v-bind="activatorProps"
                    color="primary"
                    variant="tonal"
                    size="small"
                  >
                    <v-icon start size="small">
                      {{ getMessageTypeIcon(item) }}
                    </v-icon>
                    {{ getMessageTypeLabel(item) }}
                  </v-chip>
                </template>
                <span>ID: {{ item.messageCenterMessageId }}</span>
              </v-tooltip>
            </td>
            <!--Users slot-->
            <td>
              <v-tooltip location="top">
                <template v-slot:activator="{ props: activatorProps }">
                  <div v-bind="activatorProps" class="d-flex align-center">
                    <v-icon color="primary" class="mr-1">
                      {{
                        item.userAssociationCount > 0
                          ? 'mdi-account-group'
                          : 'mdi-account-off'
                      }}
                    </v-icon>
                    <span class="text-primary text-subtitle-2">
                      {{
                        item.userAssociationCount > 0
                          ? item.userAssociationCount
                          : 'None'
                      }}
                    </span>
                  </div>
                </template>
                <span>{{
                  item.userAssociationCount > 0
                    ? `Users assigned - ${item.userAssociationCount}`
                    : 'No users assigned'
                }}</span>
              </v-tooltip>
            </td>
            <!--Title slot-->
            <td>
              <span class="text-primary text-subtitle-2 text-left">
                {{ item.messageCenterMessageLabel || '-' }}
              </span>
            </td>
            <td>
              <span class="text-primary text-subtitle-2 text-left">
                {{
                  dateTimeToUSFormatFactory(item.lastModifiedDateTime, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                  })
                }}
              </span>
            </td>
            <!--Edit slot-->
            <td>
              <v-btn
                icon="mdi-pencil"
                variant="text"
                color="primary"
                size="small"
                :loading="editingItemId === item.messageCenterMessageId"
                :disabled="isEditMessageDirty || isRowBeingEdited(item)"
                @click="editMessage(item)"
              />
            </td>
            <td>
              <ActionButtonsVue :buttonsList="actionButtons" :item="item" />
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </v-card>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';

import { dateTimeToUSFormatFactory } from '@/Core.Patterns/Factory';
import ActionButtonsVue from '@/Core.UI.Domain/Components/ActionButtons.vue';

import API from '@/Core.Service.Domain/MessageCenter/Management/API';

export default {
  name: 'MessageCenterSentMessagesList',

  computed: {
    ...mapGetters({
      messagesList: 'MessageCenterManagementStore/getMessagesList',
      getMessageToEdit: 'MessageCenterManagementStore/getMessageToEdit',
      messagesTypesList: 'MessageCenterManagementStore/getMessagesTypesList',
      isEditMessageDirty: 'MessageCenterManagementStore/getEditMessageDirty',
    }),

    isCurrentRouteEdit() {
      return this.$route.name === 'MessageCenterEdit';
    },
  },

  components: {
    ActionButtonsVue,
  },

  data(instance) {
    return {
      searchTerm: '',
      headerProps: {
        'sort-icon': 'mdi-unfold-more-horizontal',
      },
      headers: [
        {
          title: 'Type',
          align: 'start',
          class: 'text-primary text-subtitle-1 font-weight-bold',
          filterable: true,
          key: 'messageCenterMessageTypeId',
        },
        {
          title: 'Users',
          align: 'start',
          class: 'text-primary text-subtitle-1 font-weight-bold',
          filterable: true,
          key: 'userAssociationCount',
        },
        {
          title: 'Title',
          align: 'start',
          class: 'text-primary text-subtitle-1 font-weight-bold',
          filterable: true,
          key: 'messageCenterMessageLabel',
        },
        {
          title: 'Last Modified',
          align: 'start',
          class: 'text-primary text-subtitle-1 font-weight-bold',
          filterable: true,
          key: 'lastModifiedDateTime',
        },
        {
          title: 'Edit',
          align: 'start',
          class: 'text-primary text-subtitle-1 font-weight-bold',
          filterable: false,
          key: 'edit',
        },
        {
          title: 'Delete',
          align: 'start',
          class: 'text-primary text-subtitle-1 font-weight-bold',
          filterable: false,
          key: '',
        },
      ],
      bannerMessage: 'No messages found',

      actionButtons: [
        {
          icon: () => 'trash-can-outline',
          toolTip: () => 'Delete message',
          action: async (item) => {
            instance.itemIsLoading = item.messageCenterMessageId;
            await instance.deleteMessage(item);
            instance.itemIsLoading = null;
          },
          loading: (item) =>
            item.messageCenterMessageId === instance.itemIsLoading,
          disabled: (item) =>
            item.userAssociationCount > 0 || instance.isCurrentRouteEdit,
        },
      ],
      itemIsLoading: null,
      editingItemId: null,
    };
  },

  methods: {
    ...mapActions({
      editMessageFromMessageCenter:
        'MessageCenterManagementStore/editMessageFromMessageCenter',
      getAllMessagesFromMessageCenter:
        'MessageCenterManagementStore/getAllMessagesFromMessageCenter',
    }),

    dateTimeToUSFormatFactory,

    isRowBeingEdited(item) {
      return (
        this.isCurrentRouteEdit &&
        String(this.$route.params.id) === String(item.messageCenterMessageId)
      );
    },

    async editMessage(message) {
      const id = message.messageCenterMessageId;
      this.editingItemId = id;
      try {
        await this.editMessageFromMessageCenter(id);
        this.$router.push({
          name: 'MessageCenterEdit',
          params: { id },
        });
        // On stacked layouts the compose panel sits above the list; bring it
        // into view so the user sees the form they just opened.
        if (this.$vuetify.display.smAndDown) {
          this.$nextTick(() => {
            document
              .getElementById('message-compose-panel')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
      } catch (error) {
        return Promise.reject(error);
      } finally {
        this.editingItemId = null;
      }
    },

    async deleteMessage(message) {
      const id = message.messageCenterMessageId;
      try {
        await API.deleteMessage(id);
        await this.getAllMessagesFromMessageCenter();
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getMessageTypeIcon(message) {
      const iconsList = {
        1: 'mdi-text-box-outline',
        2: 'mdi-window-restore',
        3: 'mdi-image-area',
      };
      return iconsList[message.messageCenterMessageTypeId];
    },

    getMessageTypeLabel(message) {
      const label = this.messagesTypesList.find(
        (type) =>
          type.messageCenterMessageTypeId === message.messageCenterMessageTypeId
      );

      return label ? label.messageCenterMessageTypeName : '-';
    },
  },
};
</script>
<style scoped lang="scss">
.search-field {
  max-width: 320px;
  min-width: 200px;
}

// Allow the table to scroll horizontally instead of squishing on small screens
.table-scroll {
  overflow-x: auto;
}
</style>
